import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import Accordion from '../../Layout/Accordion/Accordion'
import { StoreProps } from '@/app/models/store'
import { LuxButton, LuxDataTable, LuxBadgeLabel } from '@lux/wardrobe-lib'
import { columnsGeneralTable } from './generalTable'
import { columnsQuestions } from './questions'
import { columnsTasks } from './tasks'
import { columnsSurveys } from './surveys'
import DialogRedo from '../../UI/Dialogs/DialogRedo/DialogRedo'
import { reloadProjectStatus } from '@/app/redux/actions/activity-execution'
import { NEW_ACTIVITY_EXECUTION_NAMESPACE } from '@/app/redux/reducers/activity-execution'
import { ProjectApiCreateActivityBody } from '@/app/models/projects-service'
import {
    getStatusGeneralData,
    getStoreDetailsGeneralTableData,
    getStoreDetailsQuestionData,
    getStoreDetailsSurveyData,
    getStoreDetailsTaskData,
} from '@/app/utils/activityAdapter'
import { closeAll, redoAll } from '@/app/models/new-activity-creation-cta'
import { SmartlyActionItems, SmartlyActionSubtypes, User } from '@Techedge/digitalportal-shared'
import DialogOnlyText from '../../UI/Dialogs/DialogOnlyText/DialogOnlyText'
import { columnsStatus } from './statusTable'
import { statusColors } from '@/app/utils/statusColorMap'
import Attachment from '../../UI/Attachment/Attachment'
import { columnsAnswer } from './answer'
import { downloadExcelFromList } from '@/app/utils/commonFunctions'
import { Markup } from 'interweave'
import './ApprovalDetail.scss'
import ReassignDialog from '../../ActivityExecution/ReassignDialog/ReassignDialog'
import { DelegateType } from '@/app/models/assignee'

const ApprovalDetail = (props: {
    stores: StoreProps[]
    projectId: string
    projectDetails: ProjectApiCreateActivityBody
    hasStatusList?: boolean
    user: User
    showReassignApproval: boolean
    onBack: () => void
    reloadProjectStatus: (projectId, stores, callback) => void
    onRedo: (
        storeId: string,
        question: string,
        task: string,
        survey: string,
        successCallback: () => void
    ) => void
    onClose: (storeId: string) => void
}) => {
    const {
        stores,
        hasStatusList,
        projectDetails,
        showReassignApproval,
        projectId,
        user,
        onRedo,
        onClose,
        reloadProjectStatus,
    } = props

    const componentClass = 'smart-modals-news-approval-detail'

    const intl = useIntl()

    const [openDialogRedo, setOpenDialogRedo] = useState<boolean>(false)
    const [openDialogClose, setOpenDialogClose] = useState<boolean>(false)
    const [localStores, setLocalStores] = useState<StoreProps[]>([])

    const [lazyParams, setLazyParams] = useState({
        rows: 5,
        page: 0,
        first: 0,
        sortField: null,
        sortOrder: null,
    })

    const [openReassignDialog, setOpenReassignDialog] = useState<boolean>(false)

    const onPage = (e) => {
        setLazyParams(e)
    }

    useEffect(() => {
        ;(
            document.getElementsByClassName('smart-modals-main-content__body')[0] as HTMLElement
        ).scrollTop = 0
        reloadProjectStatus(
            projectId,
            stores.map((s) => s.storeId),
            (data) => setLocalStores(data.storesList)
        )
    }, [])

    useEffect(() => {
        const newLocalStores = localStores.map((store) => ({
            ...store,
            status: stores.find((s) => s.storeId === store.storeId)?.status.key || store.status,
        }))
        setLocalStores(newLocalStores)
    }, [stores])

    const onDownloadDataList = (dataList) => {
        downloadExcelFromList(
            dataList.map((d) => ({ ...d, id: d.id + 1 })),
            'exported-table'
        )
    }

    const taskRowTemplate = (rowData) => {
        return (
            <>
                <div className={`${componentClass}__field-container description-container`}>
                    <div className={`${componentClass}__field-title`}>
                        {intl.formatMessage({
                            id: 'modals.new.forum.description',
                            defaultMessage: 'Description',
                        })}
                    </div>
                    <div className={`${componentClass}__field-value`}>
                        <div>
                            <Markup content={rowData.description} />
                        </div>
                    </div>
                </div>
                {rowData.questions.map((question) => (
                    <>
                        <div className={`${componentClass}__field-container task-content`}>
                            <div className={`${componentClass}__field-title`}>
                                {intl.formatMessage({
                                    id: 'modals.news_execution.approval.stores_detail.table.header.question',
                                    defaultMessage: 'Question',
                                })}
                            </div>
                            <div className={`${componentClass}__field-value`}>
                                <div>
                                    <Markup content={question.title} />
                                </div>
                            </div>
                        </div>
                        <div className={`${componentClass}__field-container task-content`}>
                            <div className={`${componentClass}__field-title`}>
                                {intl.formatMessage({
                                    id: 'modals.news_execution.approval.stores_detail.table.header.answer',
                                    defaultMessage: 'Answer',
                                })}
                            </div>
                            <div className={`${componentClass}__field-value`}>
                                {question.answer}
                            </div>
                        </div>
                        <div className={`${componentClass}__field-container task-content`}>
                            <div className={`${componentClass}__field-title`}>
                                {intl.formatMessage({
                                    id: 'modals.news_execution.approval.stores_detail.table.header.question_type',
                                    defaultMessage: 'Question Type',
                                })}
                            </div>
                            <div className={`${componentClass}__field-value`}>{question.type}</div>
                        </div>
                        <div className={`${componentClass}__field-container task-content`}>
                            <div className={`${componentClass}__field-title`}>
                                {intl.formatMessage({
                                    id: 'modals.news_execution.approval.stores_detail.table.header.attachments',
                                    defaultMessage: 'Attachments',
                                })}
                            </div>
                            <div className={`${componentClass}__field-value`}>
                                {question?.attachments?.length ? (
                                    <div>
                                        {question.attachments.map((a: any, i: number) => {
                                            return (
                                                <div
                                                    key={i}
                                                    style={{ marginBottom: 'var(--spacing-3)' }}>
                                                    <Attachment
                                                        fileUrl={a.url}
                                                        fileName={a.name}
                                                        fileExtension={a.ext}
                                                        showDownloadIcon={true}
                                                        // hideEyeIcon={true}
                                                    />
                                                </div>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    '/'
                                )}
                            </div>
                        </div>
                    </>
                ))}
            </>
        )
    }

    const surveyRowTemplate = (rowData) => {
        return (
            <>
                <div className={`${componentClass}__field-container description-container`}>
                    <div className={`${componentClass}__field-title`}>
                        {intl.formatMessage({
                            id: 'modals.new.forum.description',
                            defaultMessage: 'Description',
                        })}
                    </div>
                    <div className={`${componentClass}__field-value`}>
                        <div>
                            <Markup content={rowData.description} />
                        </div>
                    </div>
                </div>
                <div className={`${componentClass}__field-container survey-content`}>
                    <div className={`${componentClass}__field-title`}>
                        {intl.formatMessage({
                            id: 'modals.news_execution.approval.stores_detail.table.header.surveyUrl',
                            defaultMessage: 'Survey URL',
                        })}
                    </div>
                    <div className={`${componentClass}__field-value`}>
                        {rowData.isCompleted && (
                            <a href={rowData.surveyUrl} target="blank">
                                {rowData.surveyUrl}
                            </a>
                        )}
                        {!rowData.isCompleted && <span className="text">{rowData.surveyUrl}</span>}
                    </div>
                </div>
            </>
        )
    }

    const projectStatusTemplate = (rowData: any) => (
        <div className="smart-wardrobe-expandable-row project-status-template">
            {rowData.users &&
                Object.keys(rowData.users).map((k, i) => (
                    <div key={i} className="grid">
                        <div className="col-6">
                            <div className="exp-row-title">
                                {intl.formatMessage({
                                    id: 'modals.news_execution.approval.stores_detail.table.header.status',
                                    defaultMessage: 'Status',
                                })}
                            </div>
                            <div className="exp-row-data">
                                {intl.formatMessage({
                                    id: `modals.news_execution.approval.stores_detail.table.field.${k}`,
                                    defaultMessage: k,
                                })}
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="exp-row-title">
                                {intl.formatMessage({
                                    id: 'modals.news_execution.approval.stores_detail.table.header.users',
                                    defaultMessage: 'Users',
                                })}
                            </div>
                            <div className="exp-row-data">{rowData.users[k]}</div>
                        </div>
                    </div>
                ))}
        </div>
    )

    return (
        <>
            <div className={componentClass}>
                {localStores?.length > 0 &&
                    localStores.map((s: StoreProps, i: number) => {
                        const storeDetailData = !hasStatusList
                            ? getStoreDetailsGeneralTableData(
                                  s,
                                  intl.formatMessage({
                                      id: 'modals.news_execution.action.tasks.detail.questions',
                                      defaultMessage: 'Questions',
                                  }),
                                  intl.formatMessage({
                                      id: 'modals.news_execution.action.tasks.task',
                                      defaultMessage: 'Task',
                                  }),
                                  intl.formatMessage({
                                      id: 'modals.news_execution.action.surveys.survey',
                                      defaultMessage: 'Survey',
                                  }),
                                  intl.formatMessage({
                                      id: 'modals.activity.execution.empty.answer',
                                      defaultMessage: 'Answer',
                                  })
                              )
                            : getStatusGeneralData(
                                  projectDetails.storesList,
                                  intl.formatMessage({
                                      id:
                                          'modals.news_execution.approval.stores_detail.' +
                                          projectDetails?.projectType,
                                      defaultMessage: projectDetails?.projectType,
                                  })
                              )
                        return (
                            <Accordion key={i} activeIndex={i} title={s.storeName}>
                                <div className={`${componentClass}__info`}>
                                    {intl.formatMessage({
                                        id: 'modals.news_execution.approval.stores_detail.task_info',
                                        defaultMessage: 'Task info',
                                    })}
                                </div>
                                <div className={`${componentClass}__status`}>
                                    <LuxBadgeLabel
                                        label={intl.formatMessage({
                                            id: `smart.project.status.${s.status}`,
                                            defaultMessage: s.status,
                                        })}
                                        color={statusColors[s.status]}
                                    />
                                </div>
                                {projectDetails?.information && (
                                    <div className={`${componentClass}__description`}>
                                        {!!projectDetails && (
                                            <div>
                                                <Markup content={projectDetails?.information} />
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className={`${componentClass}__general_table`}>
                                    <h3>
                                        {intl.formatMessage({
                                            id: 'modals.news_execution.approval.stores_detail.general_table',
                                            defaultMessage: 'General table',
                                        })}
                                    </h3>
                                    <LuxDataTable
                                        lazy={true}
                                        onPage={onPage}
                                        first={lazyParams.first}
                                        dataSet={storeDetailData}
                                        columns={columnsGeneralTable.map((column) => ({
                                            ...column,
                                            header: column.header
                                                ? intl.formatMessage({
                                                      id:
                                                          column.field === 'completed_stores'
                                                              ? `${column.header}.${projectDetails?.projectType}`
                                                              : column.header,
                                                      defaultMessage: column.header
                                                          .split('.')
                                                          .pop(),
                                                  })
                                                : '',
                                        }))}
                                        scrollable={true}
                                        columnFilters={{}}
                                        totalRecords={storeDetailData.length}
                                        labels={{
                                            filter: intl.formatMessage({
                                                id: 'modals.new_resource.distribution.store_list.filter',
                                                defaultMessage: 'filter',
                                            }),
                                            rearrange: intl.formatMessage({
                                                id: 'modals.new_resource.distribution.store_list.rearrange',
                                                defaultMessage: 'rearrange',
                                            }),
                                            of: intl.formatMessage({
                                                id: 'modals.new_resource.distribution.store_list.labels.of',
                                                defaultMessage: 'of',
                                            }),
                                            selected: intl.formatMessage({
                                                id: 'modals.new_resource.distribution.store_list.labels.selected',
                                                defaultMessage: 'selected',
                                            }),
                                            emptyMessage: intl.formatMessage({
                                                id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                                                defaultMessage: 'No results to show.',
                                            }),
                                        }}
                                        onDownload={() => onDownloadDataList(storeDetailData)}
                                    />
                                </div>
                                <div className={`${componentClass}__project_details`}>
                                    <h1>
                                        {intl.formatMessage({
                                            id: 'modals.news_execution.approval.stores_detail.project_details',
                                            defaultMessage: 'Project Details',
                                        })}
                                    </h1>
                                    {s.questionsStores &&
                                        s.questionsStores[0].questionStore &&
                                        s.questionsStores[0].questionStore.length > 0 && (
                                            <>
                                                <h3>
                                                    {intl.formatMessage({
                                                        id: 'modals.news_execution.approval.stores_detail.project_details.questions',
                                                        defaultMessage: 'Questions',
                                                    })}
                                                </h3>
                                                <LuxDataTable
                                                    dataSet={getStoreDetailsQuestionData(
                                                        s.questionsStores,
                                                        intl.formatMessage({
                                                            id: 'modals.news_execution.action.action_card.status.completed',
                                                            defaultMessage: 'Completed',
                                                        }),
                                                        intl.formatMessage({
                                                            id: 'modals.news_execution.action.action_card.status.not_completed',
                                                            defaultMessage: 'Not completed',
                                                        })
                                                    )}
                                                    totalRecords={
                                                        s.questionsStores[0].questionStore.length
                                                    }
                                                    columns={columnsQuestions.map((column) => ({
                                                        ...column,
                                                        header: column.header
                                                            ? intl.formatMessage({
                                                                  id: column.header,
                                                                  defaultMessage: column.header
                                                                      .split('.')
                                                                      .pop(),
                                                              })
                                                            : '',
                                                    }))}
                                                    verticalLayout={true}
                                                    allowExpansion={true}
                                                    columnFilters={{}}
                                                    onDownload={() =>
                                                        onDownloadDataList(
                                                            getStoreDetailsQuestionData(
                                                                s.questionsStores,
                                                                intl.formatMessage({
                                                                    id: 'modals.news_execution.action.action_card.status.completed',
                                                                    defaultMessage: 'Completed',
                                                                }),
                                                                intl.formatMessage({
                                                                    id: 'modals.news_execution.action.action_card.status.not_completed',
                                                                    defaultMessage: 'Not completed',
                                                                })
                                                            )
                                                        )
                                                    }
                                                    labels={{
                                                        filter: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.filter',
                                                            defaultMessage: 'filter',
                                                        }),
                                                        rearrange: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.rearrange',
                                                            defaultMessage: 'rearrange',
                                                        }),
                                                        of: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.of',
                                                            defaultMessage: 'of',
                                                        }),
                                                        selected: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.selected',
                                                            defaultMessage: 'selected',
                                                        }),
                                                        emptyMessage: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                                                            defaultMessage: 'No results to show.',
                                                        }),
                                                    }}
                                                />
                                                <hr />
                                            </>
                                        )}
                                    {s.jobsStores && (
                                        <>
                                            <h3>
                                                {intl.formatMessage({
                                                    id: 'modals.news_execution.approval.stores_detail.project_details.tasks',
                                                    defaultMessage: 'Tasks',
                                                })}
                                            </h3>
                                            <div className={`${componentClass}__task-data-table`}>
                                                <LuxDataTable
                                                    dataSet={getStoreDetailsTaskData(
                                                        s.jobsStores,
                                                        intl.formatMessage({
                                                            id: 'modals.news_execution.action.action_card.status.completed',
                                                            defaultMessage: 'Completed',
                                                        }),
                                                        intl.formatMessage({
                                                            id: 'modals.news_execution.action.action_card.status.not_completed',
                                                            defaultMessage: 'Not completed',
                                                        }),
                                                        intl
                                                    )}
                                                    totalRecords={s.jobsStores[0].jobStore.length}
                                                    columns={columnsTasks.map((column) => ({
                                                        ...column,
                                                        header: column.header
                                                            ? intl.formatMessage({
                                                                  id: column.header,
                                                                  defaultMessage: column.header
                                                                      .split('.')
                                                                      .pop(),
                                                              })
                                                            : '',
                                                    }))}
                                                    columnFilters={{}}
                                                    verticalLayout={true}
                                                    allowExpansion={true}
                                                    rowExpansionTemplate={taskRowTemplate}
                                                    labels={{
                                                        filter: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.filter',
                                                            defaultMessage: 'filter',
                                                        }),
                                                        rearrange: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.rearrange',
                                                            defaultMessage: 'rearrange',
                                                        }),
                                                        of: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.of',
                                                            defaultMessage: 'of',
                                                        }),
                                                        selected: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.selected',
                                                            defaultMessage: 'selected',
                                                        }),
                                                        emptyMessage: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                                                            defaultMessage: 'No results to show.',
                                                        }),
                                                    }}
                                                    onDownload={() =>
                                                        onDownloadDataList(
                                                            getStoreDetailsTaskData(
                                                                s.jobsStores,
                                                                intl.formatMessage({
                                                                    id: 'modals.news_execution.action.action_card.status.completed',
                                                                    defaultMessage: 'Completed',
                                                                }),
                                                                intl.formatMessage({
                                                                    id: 'modals.news_execution.action.action_card.status.not_completed',
                                                                    defaultMessage: 'Not completed',
                                                                }),
                                                                intl
                                                            )
                                                        )
                                                    }
                                                />
                                            </div>
                                            <hr />
                                        </>
                                    )}
                                    {s.surveysStores && (
                                        <>
                                            <h3>
                                                {intl.formatMessage({
                                                    id: 'modals.news_execution.approval.stores_detail.project_details.surveys',
                                                    defaultMessage: 'Surveys',
                                                })}
                                            </h3>
                                            <div className={`${componentClass}__survey-data-table`}>
                                                <LuxDataTable
                                                    dataSet={getStoreDetailsSurveyData(
                                                        s.surveysStores,
                                                        intl.formatMessage({
                                                            id: 'modals.news_execution.action.action_card.status.completed',
                                                            defaultMessage: 'Completed',
                                                        }),
                                                        intl.formatMessage({
                                                            id: 'modals.news_execution.action.action_card.status.not_completed',
                                                            defaultMessage: 'Not completed',
                                                        }),
                                                        intl
                                                    )}
                                                    totalRecords={
                                                        s.surveysStores[0].surveysStore.length
                                                    }
                                                    columns={columnsSurveys.map((column) => ({
                                                        ...column,
                                                        header: column.header
                                                            ? intl.formatMessage({
                                                                  id: column.header,
                                                                  defaultMessage: column.header
                                                                      .split('.')
                                                                      .pop(),
                                                              })
                                                            : '',
                                                    }))}
                                                    columnFilters={{}}
                                                    verticalLayout={true}
                                                    allowExpansion={true}
                                                    rowExpansionTemplate={surveyRowTemplate}
                                                    labels={{
                                                        filter: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.filter',
                                                            defaultMessage: 'filter',
                                                        }),
                                                        rearrange: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.rearrange',
                                                            defaultMessage: 'rearrange',
                                                        }),
                                                        of: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.of',
                                                            defaultMessage: 'of',
                                                        }),
                                                        selected: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.selected',
                                                            defaultMessage: 'selected',
                                                        }),
                                                        emptyMessage: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                                                            defaultMessage: 'No results to show.',
                                                        }),
                                                    }}
                                                    onDownload={() =>
                                                        onDownloadDataList(
                                                            getStoreDetailsSurveyData(
                                                                s.surveysStores,
                                                                intl.formatMessage({
                                                                    id: 'modals.news_execution.action.action_card.status.completed',
                                                                    defaultMessage: 'Completed',
                                                                }),
                                                                intl.formatMessage({
                                                                    id: 'modals.news_execution.action.action_card.status.not_completed',
                                                                    defaultMessage: 'Not completed',
                                                                }),
                                                                intl
                                                            )
                                                        )
                                                    }
                                                />
                                            </div>
                                        </>
                                    )}
                                    {projectDetails?.projectType !== SmartlyActionItems.NEWS &&
                                        !s.questionsStores &&
                                        !s.jobsStores &&
                                        !s.surveysStores && (
                                            <>
                                                <h3>
                                                    {intl.formatMessage({
                                                        id: 'modals.activity.execution.empty.answer',
                                                        defaultMessage: 'Answer',
                                                    })}
                                                </h3>
                                                <LuxDataTable
                                                    dataSet={[
                                                        {
                                                            title: 'Answer',
                                                            completion: s.answer
                                                                ? intl.formatMessage({
                                                                      id: 'modals.news_execution.action.action_card.status.completed',
                                                                      defaultMessage: 'Completed',
                                                                  })
                                                                : intl.formatMessage({
                                                                      id: 'modals.news_execution.action.action_card.status.not_completed',
                                                                      defaultMessage:
                                                                          'Not completed',
                                                                  }),
                                                            answer: s.answer,
                                                            id: 0,
                                                        },
                                                    ]}
                                                    totalRecords={[s.answer].length}
                                                    columns={columnsAnswer.map((column) => ({
                                                        ...column,
                                                        header: column.header
                                                            ? intl.formatMessage({
                                                                  id: column.header,
                                                                  defaultMessage: column.header
                                                                      .split('.')
                                                                      .pop(),
                                                              })
                                                            : '',
                                                    }))}
                                                    columnFilters={{}}
                                                    verticalLayout={true}
                                                    allowExpansion={true}
                                                    labels={{
                                                        filter: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.filter',
                                                            defaultMessage: 'filter',
                                                        }),
                                                        rearrange: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.rearrange',
                                                            defaultMessage: 'rearrange',
                                                        }),
                                                        of: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.of',
                                                            defaultMessage: 'of',
                                                        }),
                                                        selected: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.selected',
                                                            defaultMessage: 'selected',
                                                        }),
                                                        emptyMessage: intl.formatMessage({
                                                            id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                                                            defaultMessage: 'No results to show.',
                                                        }),
                                                    }}
                                                    onDownload={() =>
                                                        onDownloadDataList([
                                                            {
                                                                title: 'Answer',
                                                                completion: s.answer
                                                                    ? intl.formatMessage({
                                                                          id: 'modals.news_execution.action.action_card.status.completed',
                                                                          defaultMessage:
                                                                              'Completed',
                                                                      })
                                                                    : intl.formatMessage({
                                                                          id: 'modals.news_execution.action.action_card.status.not_completed',
                                                                          defaultMessage:
                                                                              'Not completed',
                                                                      }),
                                                                answer: s.answer,
                                                                id: 0,
                                                            },
                                                        ])
                                                    }
                                                />
                                            </>
                                        )}
                                    {hasStatusList && (
                                        <LuxDataTable
                                            label={intl.formatMessage({
                                                id: 'modals.news_execution.approval.stores_detail.project_details.status.list',
                                                defaultMessage: 'Status list',
                                            })}
                                            dataSet={[
                                                {
                                                    title: projectDetails.projectTitle,
                                                    users: {
                                                        opened: s.visualizations?.length || 0,
                                                        notOpened:
                                                            s.members -
                                                            (s.visualizations?.length || 0),
                                                        read: s.confirmations || 0,
                                                    },
                                                },
                                            ]}
                                            columns={columnsStatus.map((column) => ({
                                                ...column,
                                                header: column.header
                                                    ? intl.formatMessage({
                                                          id: `${column.header}.${projectDetails?.projectType}`,
                                                          defaultMessage: column.header
                                                              .split('.')
                                                              .pop(),
                                                      })
                                                    : '',
                                            }))}
                                            rowExpansionTemplate={projectStatusTemplate}
                                            allowExpansion
                                            verticalLayout
                                            columnFilters={{}}
                                            containerClassName="status-table"
                                            labels={{
                                                filter: intl.formatMessage({
                                                    id: 'modals.new_resource.distribution.store_list.filter',
                                                    defaultMessage: 'filter',
                                                }),
                                                rearrange: intl.formatMessage({
                                                    id: 'modals.new_resource.distribution.store_list.rearrange',
                                                    defaultMessage: 'rearrange',
                                                }),
                                                of: intl.formatMessage({
                                                    id: 'modals.new_resource.distribution.store_list.labels.of',
                                                    defaultMessage: 'of',
                                                }),
                                                selected: intl.formatMessage({
                                                    id: 'modals.new_resource.distribution.store_list.labels.selected',
                                                    defaultMessage: 'selected',
                                                }),
                                                emptyMessage: intl.formatMessage({
                                                    id: 'modals.new_resource.distribution.store_list.labels.emptyMessage',
                                                    defaultMessage: 'No results to show.',
                                                }),
                                            }}
                                            onDownload={() =>
                                                onDownloadDataList([
                                                    {
                                                        title: projectDetails.projectTitle,
                                                        users: {
                                                            opened: s.visualizations?.length || 0,
                                                            notOpened:
                                                                s.members -
                                                                (s.visualizations?.length || 0),
                                                            read: s.confirmations || 0,
                                                        },
                                                    },
                                                ])
                                            }
                                        />
                                    )}
                                </div>
                                <div className={`${componentClass}__footer`}>
                                    {showReassignApproval && (
                                        <LuxButton
                                            variant={'outlined'}
                                            text={intl.formatMessage({
                                                id: 'ph2.modals.news_execution.button.reassign_approver',
                                                defaultMessage: 'Reassign approver',
                                            })}
                                            onClick={() => setOpenReassignDialog(true)}
                                        />
                                    )}
                                    {redoAll(
                                        projectDetails?.projectType as SmartlyActionItems,
                                        {
                                            ...projectDetails,
                                            status: s.status,
                                        },
                                        user
                                    ).show() && (
                                        <LuxButton
                                            variant={'text'}
                                            onClick={() => setOpenDialogRedo(true)}
                                            text={intl.formatMessage({
                                                id: 'modals.news_execution.button.redo',
                                                defaultMessage: 'Redo',
                                            })}
                                        />
                                    )}
                                    {closeAll(
                                        projectDetails?.projectType as SmartlyActionItems,
                                        projectDetails,
                                        user
                                    ).show() && (
                                        <LuxButton
                                            onClick={() => setOpenDialogClose(true)}
                                            variant={'outlined'}
                                            text={intl.formatMessage({
                                                id:
                                                    projectDetails.type !== 'photographic'
                                                        ? 'modals.news_execution.button.close'
                                                        : 'modals.task.photo.go.to.gallery.to.close',
                                                defaultMessage:
                                                    projectDetails.type !== 'photographic'
                                                        ? 'Close'
                                                        : 'Go to gallery to close',
                                            })}
                                        />
                                    )}
                                </div>
                                <DialogRedo
                                    dialogVisible={openDialogRedo}
                                    onConfirm={(question, task, survey) =>
                                        onRedo(s.storeId, question, task, survey, () =>
                                            setOpenDialogRedo(false)
                                        )
                                    }
                                    onHide={() => setOpenDialogRedo(false)}
                                    hideQuestion={
                                        !s?.questionsStores?.length ||
                                        !s?.questionsStores[0]?.questionStore?.length
                                    }
                                    hideSurvey={
                                        !s?.surveysStores?.length ||
                                        !s?.surveysStores[0]?.surveysStore?.length
                                    }
                                    hideTask={
                                        !s?.jobsStores?.length ||
                                        !s?.jobsStores[0]?.jobStore?.length
                                    }
                                />
                                <DialogOnlyText
                                    dialogVisible={openDialogClose}
                                    dialogTitle={intl.formatMessage({
                                        id: 'modals.activity.approval.close.store.title',
                                        defaultMessage: 'Close Store',
                                    })}
                                    dialogDescription={intl.formatMessage({
                                        id: 'modals.activity.approval.close.store.text.message',
                                        defaultMessage:
                                            'Are you sure you want to close this store?',
                                    })}
                                    onSave={() => {
                                        onClose(s.storeId)
                                        setOpenDialogClose(false)
                                    }}
                                    onHide={() => setOpenDialogClose(false)}
                                />
                            </Accordion>
                        )
                    })}
            </div>
            <ReassignDialog
                visible={openReassignDialog}
                projectDetails={projectDetails}
                storesList={stores}
                delegateWhat={DelegateType.approver}
                hideReassignmentSize
                onHide={() => setOpenReassignDialog(false)}
            />
        </>
    )
}

const mapStateToProps = (state: any) => ({
    projectId: state.layoutReducer.modals.props?.id,
    projectDetails: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].projectDetails,
    user: state.userReducer.user,
})

const mapDispatchToProps = (dispatch: any) => ({
    reloadProjectStatus: (projectId: string, stores?: string[], callback?: () => void) => {
        dispatch(reloadProjectStatus(projectId, stores, callback))
    },
})

export default connect(mapStateToProps, mapDispatchToProps)(ApprovalDetail)
